<template>
  <div id="accordionTourAfterTour" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourAfterTour" class="accordion-header">
        <button
          aria-controls="collapseTourAfterTour"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourAfterTour"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="contact" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.afterTour") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.afterTourSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourAfterTour"
        aria-labelledby="headingTourAfterTour"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourAfterTour"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <nav class="translations-tab mb-8">
            <div id="nav-tab" class="nav nav-tabs border-bottom-0" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-4 px-12 me-8 text-xs"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-after-tour-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.afterTour") }}</span>
                </label>
                <v-html-editor
                  :id="'case-tour-update-after-update-' + language.shortName"
                  ref="case-tour-update-after-update-ref"
                  :value="getDescription(language.shortName)"
                  @input="setDescription(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>

            <v-button
              :is-loading="isSaving"
              :disabled="isSaving || !isDirty"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTour"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAppLanguages, useLocaleKeyedRef, useLocaleFormDirty } from "@/composables/useAppLanguages";
import ClientTour from "@/api/objects/ClientTour";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  clientTour: ClientTour;
  caseId: number;
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const toast = useToast();
const { t } = useI18n();
const editorMedia = ref<any[]>([]);

// Prepare afterTrip object
const afterTrip = useLocaleKeyedRef(() => ({ description: "" }));

const afterTripData = props.clientTour?.after_trip_information?.translations
  || (props.clientTour as any)?.afterTripInformation?.translations
  || [];

afterTripData.forEach(({ locale, description }: { locale: string; description: string }) => {
  if (afterTrip.value.hasOwnProperty(locale)) {
    afterTrip.value[locale].description = description;
  }
});

const { languages, resolveStoredLocale } = useAppLanguages();
const currentLanguage = ref(resolveStoredLocale(localStorage.getItem("language")));
const isSaving = ref(false);
const { isDirty, setDescription, getDescription, markSaved } = useLocaleFormDirty(afterTrip);

const updateTour = async () => {
  let saved = false;
  try {
    isSaving.value = true;

    const data = {
      after_trip: afterTrip.value,
    };

    await api.case.updateTranslations(props.agency, props.caseId, data);

    if (editorMedia.value.length > 0) {
      const editorMediaPayload = {
        media: editorMedia.value,
        model_type: "client_tour",
        collection: "tinymce",
        model_id: props.clientTour.id,
      };

      await api.media.confirm("s3", editorMediaPayload);
    }

    toast.success(t("general.shared.savedChanges"));
    markSaved();
    saved = true;
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || "An error occurred");
  } finally {
    isSaving.value = false;
  }

  if (saved) {
    emit("reloadCase");
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}
</style>
